// Sección "Gancho" que se inyecta en el `ConfigurePanel` cuando el modo es
// "Video con gancho" (`showGancho`). Es una opción ADICIONAL dentro de la
// pantalla de configurar plantilla-video (que ya trae productos, modelo,
// formato, "Configurar video", etc.) — acá solo va el selector de gancho.
//
//   • Mobile: carrusel horizontal inline (estilo selector de modelos).
//   • Desktop: una fila que abre un panel lateral con scroll vertical.
//   • Debajo, el trigger "¿Qué es un gancho?" (link en desktop / botón "?" en
//     mobile) abre el explicador.
//
// SCAFFOLD: los ganchos son placeholders (la tabla no existe todavía).

import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { GanchoPicker } from './GanchoPicker';
import { GanchoExplainerSheet } from './GanchoExplainerSheet';

export interface GanchoSectionProps {
  variant: 'mobile' | 'desktop';
  /** Clase del wrapper. Default `mt-5` (uso suelto); pasá `''` cuando va
   *  dentro de un contenedor con `gap` propio (ej. la reorg desktop). */
  className?: string;
}

export const GanchoSection: React.FC<GanchoSectionProps> = ({
  variant,
  className = 'mt-5',
}) => {
  const isDesktop = variant === 'desktop';
  const [selectedGancho, setSelectedGancho] = useState<string | null>(null);
  const [ganchoPanelOpen, setGanchoPanelOpen] = useState(false);
  const [explainerOpen, setExplainerOpen] = useState(false);

  return (
    <div className={className}>
      <p className="text-[13.5px] font-semibold text-brand-black/90 mb-2.5">
        Gancho
      </p>

      {isDesktop ? (
        // Desktop: fila que abre el panel lateral.
        <button
          type="button"
          onClick={() => setGanchoPanelOpen(true)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
        >
          <span className="text-[13.5px] text-[#3C3C43]/70">Elegí un gancho</span>
          <span className="inline-flex items-center gap-2 text-[13.5px] font-medium text-brand-black/85">
            {selectedGancho ? 'Seleccionado' : 'Elegir'}
            <ChevronRight className="w-3.5 h-3.5 text-[#3C3C43]/40" />
          </span>
        </button>
      ) : (
        // Mobile: carrusel horizontal inline (estilo modelos).
        <GanchoPicker
          variant="mobile"
          selectedId={selectedGancho}
          onSelect={setSelectedGancho}
        />
      )}

      {/* "¿Qué es un gancho?" — link en desktop / botón "?" en mobile */}
      <div className="mt-2.5">
        {isDesktop ? (
          <button
            type="button"
            onClick={() => setExplainerOpen(true)}
            className="text-[12.5px] text-[#3C3C43]/45 hover:text-brand-primary underline-offset-2 hover:underline transition-colors"
          >
            ¿Qué es un gancho?
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setExplainerOpen(true)}
            aria-label="¿Qué es un gancho?"
            className="w-6 h-6 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-[#3C3C43]/55 text-[12px] font-semibold active:bg-black/[0.1] transition-colors"
          >
            ?
          </button>
        )}
      </div>

      {/* Desktop: panel lateral de selección de gancho */}
      {isDesktop && (
        <GanchoPicker
          variant="desktop"
          open={ganchoPanelOpen}
          onClose={() => setGanchoPanelOpen(false)}
          selectedId={selectedGancho}
          onSelect={(id) => {
            setSelectedGancho(id);
            setGanchoPanelOpen(false);
          }}
        />
      )}

      {/* Explicador "¿Qué es un gancho?" */}
      <GanchoExplainerSheet
        open={explainerOpen}
        onClose={() => setExplainerOpen(false)}
      />
    </div>
  );
};
